/**
 * Run IR builder — converts <w:r> elements to InlineIR nodes.
 *
 * Each run becomes one or more InlineIR entries (text, breaks, tabs, symbols).
 * Run properties are fully resolved (style + explicit) before being set on TextIR.
 */

import type { InlineIR, TextIR, ResolvedRunProperties } from "../irTypes";
import type { ParseContext } from "../../ooxmlParser/types";
import {
  parseRunPropertiesRaw,
  resolveStylePropertiesRaw,
  mergeRunProperties,
} from "./styleResolver";
import { getChildNS, W_NS } from "../../ooxmlParser/zipReader";
import { CommentTracker } from "../../ooxmlParser/commentParser";

/**
 * Parse a <w:r> element into one or more InlineIR nodes.
 *
 * @param runEl - The <w:r> element
 * @param styleRunProps - Resolved run properties from the paragraph's style chain
 * @param isHeading - Whether the parent paragraph is a heading (affects font size stripping)
 * @param ctx - Parse context (optional, for comment tracker access)
 */
export function buildRunIR(
  runEl: Element,
  styleRunProps: ResolvedRunProperties,
  isHeading: boolean = false,
  ctx?: ParseContext
): InlineIR[] {
  const result: InlineIR[] = [];

  // Parse the run's direct <w:rPr>, then layer it on top of its character style
  // (<w:rStyle>) run properties. v2 previously ignored rStyle entirely, which
  // dropped underline/bold/color contributed by character styles such as Hyperlink
  // or legal "Title-*"/clause styles — the run kept its text but lost the formatting.
  const rPrEl = getChildNS(runEl, W_NS, "rPr");
  const directProps = rPrEl ? parseRunPropertiesRaw(rPrEl) : {};

  let runProps = directProps;
  if (rPrEl && ctx) {
    const rStyleEl = getChildNS(rPrEl, W_NS, "rStyle");
    const rStyleId =
      rStyleEl?.getAttributeNS(W_NS, "val") ||
      rStyleEl?.getAttribute("w:val") ||
      undefined;
    if (rStyleId) {
      // Character-style run props (base), overridden by the run's direct props.
      const charStyleProps = resolveStylePropertiesRaw(
        rStyleId,
        ctx
      ).runProperties;
      runProps = mergeRunProperties(charStyleProps, directProps);
    }
  }

  // Compute effective properties: strip marks that match the paragraph-style
  // defaults (heading exception for fontSize).
  const effectiveProps = computeEffectiveProps(
    runProps,
    styleRunProps,
    isHeading
  );

  // Iterate children of <w:r>
  const children = runEl.childNodes;
  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (child.nodeType !== 1) continue;

    const el = child as Element;
    const localName = el.localName || el.nodeName.replace(/^w:/, "");

    switch (localName) {
      case "t":
      case "delText": {
        const text = el.textContent || "";
        if (text) {
          const textIR: TextIR = {
            kind: "text",
            text,
            ...effectiveProps,
          };
          if (localName === "delText") {
            textIR.isDeleted = true;
          }
          result.push(textIR);
        }
        break;
      }

      case "br": {
        const brType =
          el.getAttributeNS(W_NS, "type") || el.getAttribute("w:type");
        if (brType === "page") {
          result.push({ kind: "pageBreak" });
        } else {
          result.push({ kind: "hardBreak" });
        }
        break;
      }

      case "tab":
        result.push({ kind: "tab" });
        break;

      case "sym": {
        const charCode =
          el.getAttributeNS(W_NS, "char") || el.getAttribute("w:char") || "";
        const font =
          el.getAttributeNS(W_NS, "font") ||
          el.getAttribute("w:font") ||
          undefined;
        if (charCode) {
          result.push({ kind: "symbol", charCode, font });
        }
        break;
      }

      case "drawing": {
        // Image placeholder — resolved asynchronously in Stage 2
        result.push({
          kind: "imagePlaceholder",
          imageType: "drawing",
          element: el,
        });
        break;
      }

      case "pict":
      case "object": {
        // Legacy VML image placeholder
        result.push({
          kind: "imagePlaceholder",
          imageType: "vml",
          element: el,
        });
        break;
      }

      case "commentReference": {
        // Forward to V1's CommentTracker
        const commentTracker = ctx?.commentTracker as
          | CommentTracker
          | undefined;
        if (commentTracker) {
          const id =
            el.getAttributeNS(W_NS, "id") || el.getAttribute("w:id") || "";
          if (id) commentTracker.handleCommentReference(id);
        }
        break;
      }

      case "footnoteReference":
      case "endnoteReference": {
        const id =
          el.getAttributeNS(W_NS, "id") || el.getAttribute("w:id") || "";
        if (id) {
          result.push({
            kind: "footnoteRef",
            docxId: id,
            noteKind: localName === "endnoteReference" ? "endnote" : "footnote",
          });
        }
        break;
      }

      case "footnoteRef":
      case "endnoteRef":
        // The note's own number marker inside footnotes.xml/endnotes.xml —
        // the editor renders numbers itself, so these are dropped.
        break;

      default:
        // Skip unknown elements
        break;
    }
  }

  return result;
}

/**
 * Compute effective properties — only include properties that differ from the
 * paragraph style defaults. This eliminates redundant formatting on the TextIR.
 *
 * Matches V1's stripRedundantStyleMarks logic:
 * - fontSize: strip if matches style default, UNLESS it's a heading
 * - color: strip if matches style default (even for headings)
 * - fontFamily: only strip if the style font is "Times New Roman" (CSS default)
 * - highlight: always include (never stripped)
 * - bold/italic/underline/strike: strip if matches style default
 */
function computeEffectiveProps(
  merged: ResolvedRunProperties,
  styleDefaults: ResolvedRunProperties,
  isHeading: boolean
): Partial<ResolvedRunProperties> {
  const props: Partial<ResolvedRunProperties> = {};

  // Boolean toggles: only include if explicitly different from style defaults
  if (merged.bold && merged.bold !== styleDefaults.bold)
    props.bold = merged.bold;
  if (merged.italic && merged.italic !== styleDefaults.italic)
    props.italic = merged.italic;
  if (merged.underline && merged.underline !== styleDefaults.underline)
    props.underline = merged.underline;
  if (merged.strike && merged.strike !== styleDefaults.strike)
    props.strike = merged.strike;
  if (merged.subscript) props.subscript = merged.subscript;
  if (merged.superscript) props.superscript = merged.superscript;

  // fontSize: for headings, ALWAYS include (CSS heading sizes don't match Word's).
  // For body text, strip if matches style default.
  if (merged.fontSizeHalfPt !== undefined) {
    if (isHeading) {
      props.fontSizeHalfPt = merged.fontSizeHalfPt;
    } else if (merged.fontSizeHalfPt !== styleDefaults.fontSizeHalfPt) {
      props.fontSizeHalfPt = merged.fontSizeHalfPt;
    }
  }

  // color: for headings, ALWAYS include (heading styles often define a theme color).
  // For body text, strip if matches style default.
  if (merged.color) {
    if (isHeading) {
      props.color = merged.color;
    } else if (merged.color !== styleDefaults.color) {
      props.color = merged.color;
    }
  }

  // highlight: always include (never stripped by V1)
  if (merged.highlight) props.highlight = merged.highlight;

  // fontFamily: only strip if style font is "Times New Roman" (our CSS default)
  const CSS_DEFAULT_FONT = "Times New Roman";
  if (merged.fontFamily) {
    if (
      styleDefaults.fontFamily === CSS_DEFAULT_FONT &&
      merged.fontFamily === CSS_DEFAULT_FONT
    ) {
      // Strip — matches CSS default
    } else {
      props.fontFamily = merged.fontFamily;
    }
  }

  return props;
}
